import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface HomePageProps {
  showToast?: (message: string, type?: 'success' | 'error') => void;
}

export const HomePage: React.FC<HomePageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // Vérifier si on arrive avec un hash d'erreur (lien expiré)
    const hash = window.location.hash;
    if (hash.includes('error_code=otp_expired')) {
      // Rediriger vers la page de login avec un message
      navigate('/login?expired=true');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen">
      {/* Hero Section - Page d'accueil minimaliste */}
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 pt-20">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto">
            {/* Logo ou icône */}
            <div className="mb-8">
              <div className="w-20 h-20 mx-auto bg-blue-600 rounded-full flex items-center justify-center mb-6">
                <i className="fas fa-graduation-cap text-white text-3xl"></i>
              </div>
            </div>
            
            {/* Titre principal */}
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Trouve un-e tuteur-trice HEG-Genève en 60 secondes
            </h1>
            
            {/* Description de la plateforme */}
            <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed max-w-4xl mx-auto">
              tutormatch-heg est une application réalisée par l'AEHEG en partenariat avec la Haute école de gestion de Genève (HEG-Genève). 
              Cette plateforme permet aux étudiant-es de trouver de l'aide académique auprès d'autres étudiant-es plus expérimenté-es.
            </p>
            
            {/* Sous-titre */}
            <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed">
              Choisis ta filière, ton budget et un créneau horaire
            </p>
            
            {/* CTAs principaux */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/tutors')}
                className="px-8 py-4 bg-blue-600 text-white rounded-full font-bold text-lg hover:bg-blue-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 min-w-[200px]"
              >
                <i className="fas fa-search mr-3"></i>
                Trouver un-e tuteur-trice
              </button>
              
              {!user && (
                <button
                  onClick={() => navigate('/signup')}
                  className="px-8 py-4 bg-green-600 text-white rounded-full font-bold text-lg hover:bg-green-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 min-w-[200px]"
                >
                  <i className="fas fa-chalkboard-teacher mr-3"></i>
                  Devenir tuteur-trice
                </button>
              )}
            </div>
            
            {/* Informations de confiance */}
            <div className="mt-12 flex flex-wrap justify-center items-center gap-8 text-gray-500 text-sm">
              <div className="flex items-center gap-2">
                <i className="fas fa-shield-alt text-green-500"></i>
                <span>Comptes vérifiés</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="fas fa-clock text-blue-500"></i>
                <span>Réservation simple</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="fas fa-lock text-purple-500"></i>
                <span>Données protégées</span>
              </div>
            </div>

            {/* Guide rapide pour les nouveaux utilisateurs */}
            <div className="mt-16 max-w-2xl mx-auto">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-8 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                  <i className="fas fa-compass text-[#002c46] mr-2"></i>
                  Comment démarrer ?
                </h3>
                
                <div className="space-y-5 text-left">
                  {/* Étape 1: Se connecter */}
                  <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-blue-50 transition-colors">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-[#002c46] rounded-full flex items-center justify-center text-white font-bold">
                        1
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-800 font-semibold mb-1">
                        Connecte-toi en appuyant sur l'icône <i className="fas fa-sign-in-alt text-[#002c46] mx-1"></i> en haut à droite
                      </p>
                      <p className="text-sm text-gray-600">
                        Tu as déjà un compte ? Clique sur "Se connecter" dans le menu
                      </p>
                    </div>
                  </div>

                  {/* Étape 2: S'inscrire */}
                  <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-green-50 transition-colors">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold">
                        2
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-800 font-semibold mb-1">
                        Ou inscris-toi en appuyant sur l'icône <i className="fas fa-user-plus text-green-600 mx-1"></i> en haut à droite
                      </p>
                      <p className="text-sm text-gray-600">
                        Nouveau sur la plateforme ? Crée ton compte avec ton email @etu.hesge.ch
                      </p>
                    </div>
                  </div>

                  {/* Étape 3: Confirmation email */}
                  <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-purple-50 transition-colors">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                        3
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-800 font-semibold mb-1">
                        Déjà inscrit-e et cliqué sur le lien du mail ?
                      </p>
                      <p className="text-sm text-gray-600">
                        Connecte-toi normalement pour démarrer en cliquant sur l'icône <i className="fas fa-sign-in-alt text-[#002c46] mx-1"></i>
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTA vers les pages */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex flex-wrap justify-center gap-3">
                    <button
                      onClick={() => navigate('/login')}
                      className="px-6 py-2 bg-[#002c46] text-white rounded-lg font-semibold hover:bg-[#001a2a] transition-colors text-sm"
                    >
                      <i className="fas fa-sign-in-alt mr-2"></i>
                      Se connecter
                    </button>
                    <button
                      onClick={() => navigate('/signup')}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors text-sm"
                    >
                      <i className="fas fa-user-plus mr-2"></i>
                      S'inscrire
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};